import React from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {
  LoadingButton,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  NewNumberInputField,
  NewSelectField,
  NewTextInputField,
  showModal,
} from '@edna/components';
import {useOnChange} from '@edna/hooks';

import {LabelWithHint} from 'src/components';
import {LeftButton} from 'src/components/primitives';
import {Banner} from 'src/containers/Banner';
import {EU_COUNTRIES} from 'src/containers/LeadCompanyClients/definitions';
import {mapVatNumberValue} from 'src/containers/LeadCompanyClients/utils';
import {EResellerIds, ResellerGuard, useReseller} from 'src/containers/User';
import {TResolver, joiResolver} from 'src/utils/validator';

import {useGetCompanyDetailsQuery, useSaveCompanyDetailsMutation} from '../api';
import {
  EEntityType,
  EResidentCountry,
  INDIVIDUAL_TAX_NUMBER_LENGTH,
  LEGAL_ENTITY_TAX_NUMBER_LENGTH,
  TCompanyDetails,
} from '../definitions';
import {getActionName, getCompanyDetailsSchema, getResidentCountry} from '../utils';

type TProps = {
  closeModal: TEmptyFunction;
  title?: string;
};

type TDetailsProps = {
  hasTaxNumber?: boolean;
  hasOGRN?: boolean;
  entityType?: EEntityType;
  isLegalEntity?: boolean;
  countryCode?: string;
};

const ForeignCompanyDetails = React.memo<TDetailsProps>(
  ({hasTaxNumber, hasOGRN, countryCode = ''}) => {
    const {t} = useTranslation();

    const isEUCountry = EU_COUNTRIES.includes(countryCode);

    return (
      <>
        <ResellerGuard resellerIds={EResellerIds.OSK_ID}>
          <NewNumberInputField name="ogrn" label={t('CompanyDetails:ogrn')} readOnly={hasOGRN} />
        </ResellerGuard>
        <NewTextInputField name="companyName" label={t('CompanyDetails:companyName')} />
        <NewTextInputField
          name="legalAddress"
          label={
            <LabelWithHint hint={t('CompanyDetails:legalAddressHint')}>
              {t('CompanyDetails:legalAddress')}
            </LabelWithHint>
          }
        />
        {isEUCountry ? (
          <NewTextInputField
            name="taxNumber"
            label={t('CompanyDetails:vatNumber')}
            mapValue={mapVatNumberValue}
            readOnly={hasTaxNumber}
          />
        ) : (
          <NewNumberInputField
            name="taxNumber"
            label={t('CompanyDetails:taxNumber')}
            readOnly={hasTaxNumber}
          />
        )}
      </>
    );
  },
);

ForeignCompanyDetails.displayName = 'ForeignCompanyDetails';

const RusCompanyDetails = React.memo<TDetailsProps>(({hasOGRN, hasTaxNumber}) => {
  const {t} = useTranslation();

  const {watch, trigger} = useFormContext();
  const taxNumber = watch('taxNumber');
  const entityType = watch('entityType');
  const isLegalEntity = taxNumber?.length === LEGAL_ENTITY_TAX_NUMBER_LENGTH;

  React.useEffect(() => {
    if (taxNumber) {
      trigger('taxNumber');
    }
  }, [entityType]);

  return (
    <>
      {!hasTaxNumber && (
        <NewSelectField
          label={t('CompanyDetails:reminderModal.entityType')}
          name="entityType"
          options={[
            {value: EEntityType.LEGAL, label: t(`CompanyDetails:entityType.${EEntityType.LEGAL}`)},
            {
              value: EEntityType.INDIVIDUAL,
              label: t(`CompanyDetails:entityType.${EEntityType.INDIVIDUAL}`),
            },
          ]}
        />
      )}
      <NewNumberInputField
        name="taxNumber"
        label={t('CompanyDetails:taxNumber')}
        readOnly={hasTaxNumber}
      />
      <ResellerGuard resellerIds={EResellerIds.OSK_ID}>
        <NewNumberInputField name="ogrn" label={t('CompanyDetails:ogrn')} readOnly={hasOGRN} />
      </ResellerGuard>
      {isLegalEntity && <NewTextInputField name="kpp" label={t('CompanyDetails:kpp')} />}
      <NewTextInputField name="companyName" label={t('CompanyDetails:companyName')} />
      <NewTextInputField name="siteAddress" label={t('CompanyDetails:siteAddress')} />
      <NewTextInputField name="postAddress" label={t('CompanyDetails:postAddress')} />
      <NewTextInputField name="legalAddress" label={t('CompanyDetails:legalAddress')} />
    </>
  );
});

RusCompanyDetails.displayName = 'RusCompanyDetails';

const BlrCompanyDetails = React.memo<TDetailsProps>(({hasTaxNumber}) => {
  const {t} = useTranslation();

  return (
    <>
      <NewTextInputField name="companyName" label={t('CompanyDetails:companyName')} />
      <NewNumberInputField
        name="taxNumber"
        label={t('CompanyDetails:blrTaxNumber')}
        readOnly={hasTaxNumber}
      />
      <NewTextInputField name="postAddress" label={t('CompanyDetails:postAddress')} />
      <NewTextInputField name="legalAddress" label={t('CompanyDetails:legalAddress')} />
    </>
  );
});

BlrCompanyDetails.displayName = 'BlrCompanyDetails';

const KzCompanyDetails = React.memo<TDetailsProps>(({hasTaxNumber}) => {
  const {t} = useTranslation();

  return (
    <>
      <NewTextInputField name="companyName" label={t('CompanyDetails:companyName')} />
      <NewNumberInputField
        name="taxNumber"
        label={t('CompanyDetails:binNumber')}
        readOnly={hasTaxNumber}
      />
      <NewTextInputField name="siteAddress" label={t('CompanyDetails:siteAddress')} />
      <NewTextInputField name="postAddress" label={t('CompanyDetails:postAddress')} />
      <NewTextInputField name="legalAddress" label={t('CompanyDetails:legalAddress')} />
    </>
  );
});

KzCompanyDetails.displayName = 'KzCompanyDetails';

const CompanyDetailsModal = React.memo<TProps>(({closeModal, title}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const isOSKReseller = useReseller(EResellerIds.OSK_ID);

  const {data: companyDetails} = useGetCompanyDetailsQuery(supportTenantId);
  const [saveCompanyDetails, {isLoading}] = useSaveCompanyDetailsMutation();

  const hasTaxNumber = !!companyDetails?.taxNumber;
  const hasOGRN = !!companyDetails?.ogrn;

  const companyDetailsAction = React.useMemo(() => getActionName(companyDetails), [companyDetails]);

  const resolver = React.useCallback<TResolver>(
    (values, context, options) =>
      joiResolver(
        getCompanyDetailsSchema(getResidentCountry(values.country), {
          hasOgrn: isOSKReseller,
          hasTaxNumber,
          countryCode: values.country,
        }),
      )(values, context, options),
    [isOSKReseller, hasTaxNumber],
  );

  const onSubmit = React.useCallback(
    async (values: TCompanyDetails) =>
      saveCompanyDetails({supportTenantId, data: values})
        .unwrap()
        .then(() => closeModal()),
    [supportTenantId, saveCompanyDetails],
  );

  const defaultValues = React.useMemo(() => {
    if (!companyDetails) {
      return {entityType: EEntityType.LEGAL};
    }

    const entityType =
      companyDetails.taxNumber?.length === INDIVIDUAL_TAX_NUMBER_LENGTH
        ? EEntityType.INDIVIDUAL
        : EEntityType.LEGAL;

    return {...companyDetails, entityType};
  }, [companyDetails]);

  const methods = useForm<TCompanyDetails>({
    mode: 'all',
    defaultValues,
    resolver,
  });

  const residentCountry = getResidentCountry(companyDetails?.country);

  useOnChange(() => methods.reset(defaultValues), [defaultValues]);

  const taxNumberWarningLabel = React.useMemo(() => {
    if (companyDetails?.country && EU_COUNTRIES.includes(companyDetails?.country)) {
      return 'vatNumberWarning';
    }

    switch (companyDetails?.country) {
      case EResidentCountry.BLR:
        return 'blrTaxNumberWarning';
      case EResidentCountry.KAZ:
        return 'binNumberWarning';
      default:
        return 'taxNumberWarning';
    }
  }, [companyDetails?.country]);

  return (
    <Modal width="582px">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <ModalHeader>
            {title ??
              t(`CompanyDetails:detailsAction.${companyDetailsAction}`, {
                detailsName: t('CompanyDetails:modalTitle.companyDetails'),
              })}
          </ModalHeader>
          <ModalContent>
            {!hasTaxNumber && (
              <Banner type="WARNING" shadowSize="XS" fontSize="S">
                {t(`CompanyDetails:reminderModal.${taxNumberWarningLabel}`)}
              </Banner>
            )}
            {residentCountry === EResidentCountry.FOREIGN && (
              <ForeignCompanyDetails
                countryCode={companyDetails?.country}
                hasTaxNumber={hasTaxNumber}
                hasOGRN={hasOGRN}
              />
            )}
            {residentCountry === EResidentCountry.RUS && (
              <RusCompanyDetails hasTaxNumber={hasTaxNumber} hasOGRN={hasOGRN} />
            )}
            {residentCountry === EResidentCountry.BLR && (
              <BlrCompanyDetails hasTaxNumber={hasTaxNumber} />
            )}
            {residentCountry === EResidentCountry.KAZ && (
              <KzCompanyDetails hasTaxNumber={hasTaxNumber} />
            )}
            <ModalActions>
              <LeftButton onClick={closeModal} data-selector="Common:button.cancel">
                {t('Common:button.cancel')}
              </LeftButton>
              <LoadingButton
                appearance="primary"
                type="submit"
                data-selector="Common:button.save"
                disabled={isLoading}
                loading={isLoading}
              >
                {t('Common:button.save')}
              </LoadingButton>
            </ModalActions>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  );
});

export const showCompanyDetailsModal = (title?: string) =>
  showModal((closeModal) => <CompanyDetailsModal closeModal={closeModal} title={title} />);
